import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';
import { Attachments } from '../components/Attachments.js';

type Address = { uuid: string; kind: string; line: string; from_date: string | null; to_date: string | null; nights: number | null; ledger_seq: number;
  structured?: number; line1?: string | null; line2?: string | null; city?: string | null; postcode?: string | null; country?: string | null };
type NameChange = { uuid: string; new_name: string; kind: string; change_date: string; ledger_seq: number };
type IdDoc = {
  uuid: string; doc_type: string; label: string | null;
  issued_date: string | null; expiry_date: string | null;
  doc_number: string | null; issuing_authority: string | null;
  valid_from: string | null; valid_to: string | null;
  name_as_appears: string | null; dob_as_appears: string | null;
  ledger_seq: number;
};
type CaseProfile = {
  dob: string | null; place_of_birth: string | null; ni_number: string | null;
  cro_or_pnc: string | null; offence: string | null; offence_date: string | null;
  conviction_date: string | null; sentence_date: string | null; court: string | null;
  telephone: string | null; mobile: string | null; email: string | null;
  notification_expiry: string | null; updated_at: string | null;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const DEFAULTS: Partial<CaseProfile> = {
  offence: 'Making indecent photographs of a child',
  conviction_date: '2022-10-25',
  court: 'Liverpool Crown Court',
  dob: '1983-06-12',
  place_of_birth: 'Blackpool',
};

export function Personal() {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [names, setNames] = useState<NameChange[]>([]);
  const [docs, setDocs] = useState<IdDoc[]>([]);
  const [profile, setProfile] = useState<CaseProfile | null>(null);

  async function load() {
    const [a, n, d, p] = await Promise.all([
      fetch('/api/addresses').then((r) => r.json()).catch(() => ({})),
      fetch('/api/name-changes').then((r) => r.json()).catch(() => ({})),
      fetch('/api/id-documents').then((r) => r.json()).catch(() => ({})),
      fetch('/api/case-profile').then((r) => r.json()).catch(() => ({})),
    ]);
    setAddresses(a.addresses ?? []);
    setNames(n.name_changes ?? []);
    setDocs(d.id_documents ?? []);
    setProfile(p.profile ?? null);
  }
  useEffect(() => { load(); }, []);

  return (
    <Page eyebrow="Records" title="Personal details" actions={<span className="pill pill-ok"><span className="dot dot-ok" />anchored · drives notification reminders</span>}>
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Record a change of home address, a stay of 7+ days elsewhere, a change of name or alias, or a
          new identity document. Each one raises a 3-day notification deadline on your dashboard (a stay
          must be notified within 3 days of reaching 7 days). Recording the actual notification — which is
          anchored — is what clears it.
        </p>
      </section>

      <CaseProfileBlock profile={profile} onDone={load} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }} className="dev-grid">
        <AddressBlock items={addresses} onDone={load} />
        <div style={{ display: 'grid', gap: 16 }}>
          <NameBlock items={names} onDone={load} />
          <DocBlock items={docs} onDone={load} />
        </div>
      </div>
    </Page>
  );
}

function post(url: string, body: unknown) {
  return fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
}

function CaseProfileBlock({ profile, onDone }: { profile: CaseProfile | null; onDone: () => void }) {
  const [dob, setDob] = useState(profile?.dob ?? DEFAULTS.dob ?? '');
  const [place, setPlace] = useState(profile?.place_of_birth ?? DEFAULTS.place_of_birth ?? '');
  const [ni, setNi] = useState(profile?.ni_number ?? '');
  const [cro, setCro] = useState(profile?.cro_or_pnc ?? '');
  const [offence, setOffence] = useState(profile?.offence ?? DEFAULTS.offence ?? '');
  const [offenceDate, setOffenceDate] = useState(profile?.offence_date ?? '');
  const [convictionDate, setConvictionDate] = useState(profile?.conviction_date ?? DEFAULTS.conviction_date ?? '');
  const [sentenceDate, setSentenceDate] = useState(profile?.sentence_date ?? '');
  const [court, setCourt] = useState(profile?.court ?? DEFAULTS.court ?? '');
  const [telephone, setTelephone] = useState(profile?.telephone ?? '');
  const [mobile, setMobile] = useState(profile?.mobile ?? '');
  const [email, setEmail] = useState(profile?.email ?? '');
  const [expiry, setExpiry] = useState(profile?.notification_expiry ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Sync state when profile loads
  useEffect(() => {
    if (!profile) return;
    setDob(profile.dob ?? DEFAULTS.dob ?? '');
    setPlace(profile.place_of_birth ?? DEFAULTS.place_of_birth ?? '');
    setNi(profile.ni_number ?? '');
    setCro(profile.cro_or_pnc ?? '');
    setOffence(profile.offence ?? DEFAULTS.offence ?? '');
    setOffenceDate(profile.offence_date ?? '');
    setConvictionDate(profile.conviction_date ?? DEFAULTS.conviction_date ?? '');
    setSentenceDate(profile.sentence_date ?? '');
    setCourt(profile.court ?? DEFAULTS.court ?? '');
    setTelephone(profile.telephone ?? '');
    setMobile(profile.mobile ?? '');
    setEmail(profile.email ?? '');
    setExpiry(profile.notification_expiry ?? '');
  }, [profile]);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null); setSaved(false); setBusy(true);
    try {
      const r = await post('/api/case-profile', {
        dob: dob || null, place_of_birth: place || null, ni_number: ni || null,
        cro_or_pnc: cro || null, offence: offence || null, offence_date: offenceDate || null,
        conviction_date: convictionDate || null, sentence_date: sentenceDate || null,
        court: court || null, telephone: telephone || null, mobile: mobile || null,
        email: email || null, notification_expiry: expiry || null,
      });
      if (!r.ok) { setErr((await r.json()).error ?? 'Failed'); return; }
      setSaved(true); onDone();
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div className="panel-head"><h3>Case details (notification cover sheet)</h3></div>
      <form className="card-pad" onSubmit={submit}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Date of birth</span>
            <input type="date" value={dob} max={todayISO()} onChange={(e) => setDob(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '2 1 160px' }}><span>Place of birth</span>
            <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Town / city" />
          </label>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>NI number</span>
            <input value={ni} onChange={(e) => setNi(e.target.value)} placeholder="AB 12 34 56 C" />
          </label>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>CRO / PNC number</span>
            <input value={cro} onChange={(e) => setCro(e.target.value)} placeholder="CRO or PNC" />
          </label>
        </div>
        <label className="fld"><span>Offence</span>
          <input value={offence} onChange={(e) => setOffence(e.target.value)} placeholder="Offence description" />
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Offence date</span>
            <input type="date" value={offenceDate} onChange={(e) => setOffenceDate(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Conviction date</span>
            <input type="date" value={convictionDate} onChange={(e) => setConvictionDate(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Sentence date</span>
            <input type="date" value={sentenceDate} onChange={(e) => setSentenceDate(e.target.value)} />
          </label>
        </div>
        <label className="fld"><span>Court</span>
          <input value={court} onChange={(e) => setCourt(e.target.value)} placeholder="Court name" />
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Telephone</span>
            <input value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="Landline" />
          </label>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Mobile</span>
            <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile number" />
          </label>
          <label className="fld" style={{ flex: '2 1 160px' }}><span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" />
          </label>
        </div>
        <label className="fld" style={{ maxWidth: 200 }}><span>Notification period expiry</span>
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </label>
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5 }}>{err}</div>}
        {saved && <div style={{ color: 'var(--ok)', fontSize: 12.5 }}>Saved.</div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save case details'}</button>
        {profile?.updated_at && <span className="muted" style={{ fontSize: 12, marginLeft: 12 }}>Last saved {profile.updated_at.slice(0, 16).replace('T', ' ')}</span>}
      </form>
    </div>
  );
}

function AddressBlock({ items, onDone }: { items: Address[]; onDone: () => void }) {
  const [kind, setKind] = useState<'home' | 'stay'>('home');
  const [line1, setLine1] = useState(''); const [line2, setLine2] = useState('');
  const [city, setCity] = useState(''); const [postcode, setPostcode] = useState(''); const [country, setCountry] = useState('');
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(''); const [nights, setNights] = useState('');
  const [childPresent, setChildPresent] = useState(false); const [childDetails, setChildDetails] = useState('');
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!line1.trim() && !postcode.trim()) { setErr('Enter at least the street line or postcode.'); return; }
    setBusy(true);
    try {
      const r = await post('/api/addresses', { kind, from_date: from,
        ...(line1.trim() ? { line1 } : {}), ...(line2.trim() ? { line2 } : {}), ...(city.trim() ? { city } : {}),
        ...(postcode.trim() ? { postcode } : {}), ...(country.trim() ? { country } : {}),
        ...(to ? { to_date: to } : {}), ...(nights ? { nights: Number(nights) } : {}),
        ...(childPresent ? { child_under_18_present: true, ...(childDetails.trim() ? { child_details: childDetails } : {}) } : {}) });
      if (!r.ok) { setErr((await r.json()).error ?? 'Failed'); return; }
      setLine1(''); setLine2(''); setCity(''); setPostcode(''); setCountry(''); setTo(''); setNights(''); setChildPresent(false); setChildDetails(''); onDone();
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="panel-head"><h3>Addresses & stays</h3><span className="hint">{items.length}</span></div>
      <form className="card-pad" onSubmit={submit} style={{ borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 120px' }}><span>Type</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'home' | 'stay')}>
              <option value="home">New home address</option><option value="stay">Stay elsewhere (7+ days)</option>
            </select>
          </label>
          <label className="fld" style={{ flex: '1 1 120px' }}><span>{kind === 'home' ? 'Moved in' : 'Stay from'}</span>
            <input type="date" value={from} max={todayISO()} onChange={(e) => setFrom(e.target.value)} />
          </label>
        </div>
        <label className="fld"><span>Street / building</span>
          <input value={line1} onChange={(e) => setLine1(e.target.value)} placeholder="e.g. 28 Severn Road" />
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '2 1 140px' }}><span>Line 2 (optional)</span>
            <input value={line2} onChange={(e) => setLine2(e.target.value)} placeholder="Flat / area" />
          </label>
          <label className="fld" style={{ flex: '2 1 140px' }}><span>Town / city</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Blackpool" />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 110px' }}><span>Postcode</span>
            <input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="FY1 4EJ" />
          </label>
          <label className="fld" style={{ flex: '1 1 110px' }}><span>Country</span>
            <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="UK" />
          </label>
        </div>
        <p className="muted3" style={{ fontSize: 11.5, margin: '2px 0 6px' }}>Address components are encrypted at rest. The 3-day notification deadline keys off the date above.</p>
        <label className="fld" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={childPresent} onChange={(e) => setChildPresent(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
          <span style={{ margin: 0 }}>A child under 18 lives or stays here — notifiable within 3 days (R10)</span>
        </label>
        {childPresent && (
          <label className="fld"><span>Child / household detail (optional)</span>
            <input value={childDetails} onChange={(e) => setChildDetails(e.target.value)} placeholder="e.g. partner's child, age 8" />
          </label>
        )}
        {kind === 'stay' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="fld" style={{ flex: 1 }}><span>To (optional)</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <label className="fld" style={{ flex: 1 }}><span>Nights</span><input type="number" value={nights} onChange={(e) => setNights(e.target.value)} placeholder="e.g. 10" /></label>
          </div>
        )}
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5 }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Record'}</button>
      </form>
      <div className="ledger">
        {items.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>None.</div>}
        {items.map((a) => (
          <div className="ledger-row" key={a.uuid}>
            <span className={`badge badge-${a.kind === 'home' ? 'ok' : 'mute'}`}>{a.kind.toUpperCase()}</span>
            <div className="ledger-main"><div className="ledger-text"><strong>{a.line}</strong></div>
              <div className="ledger-time">{a.from_date ?? ''}{a.to_date ? ` → ${a.to_date}` : ''}{a.nights ? ` · ${a.nights} nights` : ''}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NameBlock({ items, onDone }: { items: NameChange[]; onDone: () => void }) {
  const [name, setName] = useState(''); const [kind, setKind] = useState<'legal' | 'alias'>('legal');
  const [date, setDate] = useState(todayISO()); const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (!name.trim()) { setErr('Enter the name.'); return; }
    setBusy(true);
    try { const r = await post('/api/name-changes', { new_name: name, kind, change_date: date }); if (!r.ok) { setErr((await r.json()).error ?? 'Failed'); return; } setName(''); onDone(); } finally { setBusy(false); }
  }
  return (
    <div className="card">
      <div className="panel-head"><h3>Name & aliases</h3><span className="hint">{items.length}</span></div>
      <form className="card-pad" onSubmit={submit} style={{ borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '2 1 160px' }}><span>New name / alias</span><input value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="fld" style={{ flex: '1 1 100px' }}><span>Type</span><select value={kind} onChange={(e) => setKind(e.target.value as 'legal' | 'alias')}><option value="legal">Legal</option><option value="alias">Alias</option></select></label>
          <label className="fld" style={{ flex: '1 1 120px' }}><span>Date</span><input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} /></label>
        </div>
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5 }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Record'}</button>
      </form>
      <div className="ledger">
        {items.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>None.</div>}
        {items.map((n) => (
          <div className="ledger-row" key={n.uuid}><span className="badge badge-mute">{n.kind.toUpperCase()}</span>
            <div className="ledger-main"><div className="ledger-text"><strong>{n.new_name}</strong></div><div className="ledger-time">{n.change_date}</div></div></div>
        ))}
      </div>
    </div>
  );
}

function DocBlock({ items, onDone }: { items: IdDoc[]; onDone: () => void }) {
  const [type, setType] = useState('passport'); const [label, setLabel] = useState('');
  const [issued, setIssued] = useState(todayISO()); const [expiry, setExpiry] = useState('');
  const [docNumber, setDocNumber] = useState(''); const [issuingAuthority, setIssuingAuthority] = useState('');
  const [validFrom, setValidFrom] = useState(''); const [validTo, setValidTo] = useState('');
  const [nameAsAppears, setNameAsAppears] = useState(''); const [dobAsAppears, setDobAsAppears] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const r = await post('/api/id-documents', {
        doc_type: type, label,
        issued_date: issued, ...(expiry ? { expiry_date: expiry } : {}),
        ...(docNumber ? { doc_number: docNumber } : {}),
        ...(issuingAuthority ? { issuing_authority: issuingAuthority } : {}),
        ...(validFrom ? { valid_from: validFrom } : {}),
        ...(validTo ? { valid_to: validTo } : {}),
        ...(nameAsAppears ? { name_as_appears: nameAsAppears } : {}),
        ...(dobAsAppears ? { dob_as_appears: dobAsAppears } : {}),
      });
      if (!r.ok) { setErr((await r.json()).error ?? 'Failed'); return; }
      setLabel(''); setDocNumber(''); setIssuingAuthority('');
      setValidFrom(''); setValidTo(''); setNameAsAppears(''); setDobAsAppears('');
      onDone();
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="panel-head"><h3>ID documents</h3><span className="hint">{items.length}</span></div>
      <form className="card-pad" onSubmit={submit} style={{ borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="passport">Passport</option>
              <option value="driving_licence">Driving licence</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="fld" style={{ flex: '2 1 140px' }}><span>Label</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. UK passport" />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 140px' }}><span>Document number</span>
            <input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="Full document number" />
          </label>
          <label className="fld" style={{ flex: '2 1 160px' }}><span>Issuing authority</span>
            <input value={issuingAuthority} onChange={(e) => setIssuingAuthority(e.target.value)} placeholder="e.g. HMPO" />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Issued</span>
            <input type="date" value={issued} max={todayISO()} onChange={(e) => setIssued(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Expiry</span>
            <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Valid from</span>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </label>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>Valid to</span>
            <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label className="fld" style={{ flex: '1 1 160px' }}><span>Name as appears on document</span>
            <input value={nameAsAppears} onChange={(e) => setNameAsAppears(e.target.value)} placeholder="Exactly as printed" />
          </label>
          <label className="fld" style={{ flex: '1 1 130px' }}><span>DOB as appears on document</span>
            <input value={dobAsAppears} onChange={(e) => setDobAsAppears(e.target.value)} placeholder="e.g. 12 JUN 1983" />
          </label>
        </div>
        {err && <div style={{ color: 'var(--bad)', fontSize: 12.5 }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Record'}</button>
      </form>
      <div className="ledger">
        {items.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>None.</div>}
        {items.map((d) => (
          <div className="ledger-row" key={d.uuid}>
            <span className="badge badge-mute">{d.doc_type.replace('_', ' ').toUpperCase()}</span>
            <div className="ledger-main">
              <div className="ledger-text"><strong>{d.label ?? d.doc_type}</strong>{d.doc_number ? <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 13 }}>{d.doc_number}</span> : null}</div>
              <div className="ledger-time">
                {d.issued_date ? `issued ${d.issued_date}` : ''}
                {d.expiry_date ? ` · expires ${d.expiry_date}` : ''}
                {d.valid_from ? ` · valid ${d.valid_from}` : ''}
                {d.valid_to ? ` → ${d.valid_to}` : ''}
                {d.issuing_authority ? ` · ${d.issuing_authority}` : ''}
              </div>
              {(d.name_as_appears || d.dob_as_appears) && (
                <div className="ledger-time">
                  {d.name_as_appears ? `Name: ${d.name_as_appears}` : ''}
                  {d.name_as_appears && d.dob_as_appears ? ' · ' : ''}
                  {d.dob_as_appears ? `DOB: ${d.dob_as_appears}` : ''}
                </div>
              )}
            </div>
            <Attachments entityType="id_document" entityUuid={d.uuid} />
          </div>
        ))}
      </div>
    </div>
  );
}
